import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as yup from 'yup';
import { Header, Modal } from '../../components';
import { Search } from '../../components/Header/components';
import { Block, Button, Text, TextInput } from '../../components/layout';
import { SvgIconModalDelete } from '../../svgs';
import validateFormSchema from '../../utils/validateFormSchema';
import FileSelectItem from './components/FileSelectItem';

function Files() {
    const paginate = useRef(false);
    const [search, setSearch] = useState("");

    const hiddenFileInput = useRef(null);

    const [modalUpdateData, setModalUpdateData] = useState(null);
    const [modalDeleteData, setModalDeleteData] = useState(null);

    const [result, setResult] = useState({ edges: [], pageInfo: { hasNextPage: false }, totalCount: 0 });

    const [queryFiles, { loading }] = useLazyQuery(
        gql`
            query ($first: Int, $after: String, $search: String) {
                files(first: $first, after: $after, search: $search) {
                    edges {
                        node {
                            id
                            name
                            mimetype
                            encoding
                            url
                        }
                        cursor
                    }
                    pageInfo {
                        startCursor
                        endCursor
                        hasNextPage
                    }
                    totalCount
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.files) {
                    setResult({
                        edges: paginate.current ? [...result.edges, ...data.files.edges] : data.files.edges,
                        pageInfo: data.files.pageInfo,
                        totalCount: data.files.totalCount
                    });
                    paginate.current = false;
                }
            }
        }
    );


    const [mutationFileCreate, { loading: loadingFileCreate }] = useMutation(
        gql`
            mutation fileCreate($data: Upload!) {
                fileCreate(data: $data) {
                    id
                    name
                    mimetype
                    url
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.fileCreate) {
                    result.edges.unshift({ cursor: data.fileCreate.id, node: data.fileCreate });
                    setResult({ ...result });
                }
            }
        }
    );

    const [mutationFileUpdate, { loading: loadingFileUpdate }] = useMutation(
        gql`
                mutation fileUpdate($id: ID!, $name: String) {
                    fileUpdate(id: $id, name: $name) {
                        id
                        name
                        mimetype
                        encoding
                        url
                    }
                }
            `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.fileUpdate) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.fileUpdate.id);
                    if (index !== -1) {
                        result.edges[index].node = { ...data.fileUpdate };
                        setModalUpdateData(null);
                        setResult([...data]);
                    }
                }
            }
        }
    );

    const [mutationFileDelete, { loading: loadingFileDelete }] = useMutation(
        gql`
            mutation fileDelete($id: ID!) {
                fileDelete(id: $id) {
                    id
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.fileDelete) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.fileDelete.id);
                    if (index !== -1) {
                        result.edges.splice(index, 1);
                        setModalDeleteData(null);
                        setResult([...data]);
                    }
                }
            }
        }
    );

    const requestData = (params = { next: false }) => {
        const { first, after, next } = params;

        if (next) {
            paginate.current = true;
        }

        queryFiles({
            variables: {
                first: first || 30,
                after: after || undefined,
                search: search !== "" ? search : undefined
            }
        });
    }

    useEffect(() => {
        requestData();
    }, [search]);

    return (
        <>
            <input ref={hiddenFileInput} type="file" style={{ display: "none" }} onChange={(e) => {
                if (e.target.files[0]) {
                    mutationFileCreate({ variables: { data: e.target.files[0] } });
                    hiddenFileInput.current.value = null;
                }
            }} />
            <Block flex height="100%">
                <Header
                    leftChildren={<Search onChange={(e) => setSearch(e.target.value)} />}
                    rightChildren={
                        <Block row>
                            <Button style={{ minWidth: 309 }} text="Add File" loading={loadingFileCreate} onClick={() => hiddenFileInput.current.click()} />
                        </Block>
                    }
                />
                <Block row mt={64} style={{ flexGrow: 1, overflow: "hidden" }}>
                    <InfiniteScroll
                        height="100%"
                        dataLength={result.edges.length}
                        next={() => requestData({ next: true, after: result.pageInfo.endCursor })}
                        hasMore={result.pageInfo.hasNextPage}
                    >
                        <Block row style={{ flexWrap: "wrap", overflow: "auto" }} ml={10} mr={10}>
                            {result.edges.map((edge) => (
                                <FileSelectItem
                                    key={edge.cursor}
                                    name={edge.node.name}
                                    mimetype={edge.node.mimetype}
                                    url={edge.node.url}
                                    onClickUpdate={() => setModalUpdateData(edge.node)}
                                    onClickDelete={() => setModalDeleteData(edge.cursor)}
                                />
                            ))}
                        </Block>
                    </InfiniteScroll>
                </Block>
            </Block>
            <Modal
                title="Update File"
                isOpen={!!modalUpdateData}
                onRequestClose={() => setModalUpdateData(null)}
            >
                <Text size={14} height={20}>File</Text>
                <Block mt={28}>
                    <Formik
                        enableReinitialize
                        initialValues={modalUpdateData}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    name: yup.string().required()
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationFileUpdate({ variables: { id: modalUpdateData.id, ...values } });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block>
                                        <Block>
                                            <Block>
                                                <TextInput name="name" label="Name" type="text" onChange={handleChange} onBlur={handleBlur} value={values.name} error={errors.name} />
                                            </Block>
                                        </Block>
                                        <Block mt={44}>
                                            <Button type="submit" text="Submit" loading={loadingFileUpdate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </Modal>
            <Modal
                width={280}
                title="Delete File"
                isOpen={!!modalDeleteData}
                onRequestClose={() => setModalDeleteData(null)}
            >
                <Block mb={20}>
                    <SvgIconModalDelete />
                </Block>
                <Text size={18} height={24}>Are you sure that you want to delete this file?</Text>
                <Text size={12} height={14} mt={8}>This action it’s irreversible, it will erase all the data from this file.</Text>
                <Block mt={32}>
                    <Button text="Delete" loading={loadingFileDelete} onClick={() => mutationFileDelete({ variables: { id: modalDeleteData } })} />
                </Block>
            </Modal>
        </>
    );
}

export default Files;